/**
 * Module dependencies.
 */

import { AvatarCropDialog } from './avatar-crop-dialog';
import { Avatar } from 'app/components/ui/avatar';
import { Button } from 'app/components/ui/button';
import { Camera, Check, KeyRound, Loader2 } from 'lucide-react';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Me } from 'app/hooks/requests/use-me';
import { PasswordDialog } from './password-dialog';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useTranslation } from 'react-i18next';
import { useUploadImage } from 'app/hooks/requests/use-upload-image';
import React, { useEffect, useRef, useState } from 'react';

/**
 * Editable text fields.
 */

const editableFields = ['firstName', 'lastName'] as const;

type EditableField = (typeof editableFields)[number];

/**
 * Update payload accepted by the editor.
 */

export type EditorUpdate = Partial<Pick<Me, 'firstName' | 'lastName' | 'profilePhotoUrl' | 'isAdmin'>>;

/**
 * `Props` type.
 */

type Props = {
  user: Partial<Me> | undefined;
  onUpdate: (payload: EditorUpdate) => Promise<unknown>;
  onSetPassword: (password: string) => Promise<unknown>;
  canEditAdmin?: boolean;
};

/**
 * Resolve the user's initials.
 */

function resolveInitials(user: Partial<Me> | undefined): string {
  const source: string = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'U';

  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase())
    .join('');
}

/**
 * `ReadonlyRow` component.
 */

function ReadonlyRow({ label, value }: { label: string; value?: string }): JSX.Element {
  return (
    <div className={'space-y-1.5'}>
      <Label>{label}</Label>

      <div className={'flex h-10 items-center rounded-md bg-muted px-3 text-sm text-muted-foreground'}>
        {value || '—'}
      </div>
    </div>
  );
}

/**
 * Export `ProfileEditor` component — the shared profile editor used by the
 * current user's Settings ▸ Profile and by Administration ▸ user detail.
 */

export function ProfileEditor({ user, onUpdate, onSetPassword, canEditAdmin }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const uploadImage = useUploadImage();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const saved: Record<EditableField, string> = {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? ''
  };

  const [form, setForm] = useState<Record<EditableField, string>>(saved);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    setForm({ firstName: user?.firstName ?? '', lastName: user?.lastName ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const isFieldDirty = (field: EditableField) => {
    const value = form[field].trim();

    return value !== '' && value !== saved[field];
  };

  const dirtyFields = editableFields.filter(isFieldDirty);
  const isDirty = dirtyFields.length > 0;

  const persist = async (payload: EditorUpdate) => {
    setStatus('saving');

    try {
      await onUpdate(payload);

      setStatus('saved');
      setTimeout(() => setStatus('idle'), 1500);
    } catch {
      setStatus('idle');
      error({ message: translate('settings.profile.saveFailed') });
    }
  };

  const commitField = (field: EditableField) => {
    if (isFieldDirty(field)) {
      persist({ [field]: form[field].trim() });
    }
  };

  const saveAll = () => {
    if (!isDirty) {
      return;
    }

    persist(Object.fromEntries(dirtyFields.map(field => [field, form[field].trim()])));
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;

  // The account accepts its signup method plus password once one is set.
  const authMethods = Array.from(new Set([user?.auth, user?.hasPassword ? 'password' : null].filter(Boolean)))
    .map(method => (method as string).charAt(0).toUpperCase() + (method as string).slice(1))
    .join(', ');

  const onPickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = '';

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => setCropSrc(reader.result as string);
    reader.readAsDataURL(file);
  };

  const onCropped = async (file: File) => {
    setSavingPhoto(true);

    try {
      const url = await uploadImage.mutateAsync(file);

      await onUpdate({ profilePhotoUrl: url });

      success({ message: translate('settings.profile.photo.updated') });
      setCropSrc(null);
    } catch {
      error({ message: translate('settings.profile.photo.failed') });
    } finally {
      setSavingPhoto(false);
    }
  };

  return (
    <div className={'space-y-5'}>
      {/* Identity header */}
      <div className={'flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-sm'}>
        <div className={'group relative'}>
          <Avatar className={'h-16 w-16 text-xl'} initials={resolveInitials(user)} src={user?.profilePhotoUrl} />

          <button
            className={
              'absolute inset-0 grid place-items-center rounded-full bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100'
            }
            onClick={() => fileInputRef.current?.click()}
            type={'button'}
          >
            <Camera className={'h-5 w-5'} />
          </button>
        </div>

        <div className={'min-w-0'}>
          <div className={'truncate text-lg font-semibold text-foreground'}>{fullName}</div>

          <div className={'truncate text-sm text-muted-foreground'}>{user?.email}</div>

          <Button className={'mt-2 h-auto p-0 text-xs'} onClick={() => fileInputRef.current?.click()} variant={'link'}>
            {translate('settings.profile.photo.change')}
          </Button>
        </div>

        <input accept={'image/*'} className={'hidden'} onChange={onPickFile} ref={fileInputRef} type={'file'} />
      </div>

      {/* Account details */}
      <div className={'space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm'}>
        <div className={'text-sm font-semibold text-foreground'}>{translate('settings.profile.accountDetails')}</div>

        <div className={'grid gap-4 sm:grid-cols-2'}>
          <div className={'space-y-1.5'}>
            <Label htmlFor={'firstName'}>{translate('settings.profile.items.firstName')}</Label>

            <Input
              id={'firstName'}
              onBlur={() => commitField('firstName')}
              onChange={event => setForm(current => ({ ...current, firstName: event.target.value }))}
              onKeyDown={event => event.key === 'Enter' && event.currentTarget.blur()}
              value={form.firstName}
            />
          </div>

          <div className={'space-y-1.5'}>
            <Label htmlFor={'lastName'}>{translate('settings.profile.items.lastName')}</Label>

            <Input
              id={'lastName'}
              onBlur={() => commitField('lastName')}
              onChange={event => setForm(current => ({ ...current, lastName: event.target.value }))}
              onKeyDown={event => event.key === 'Enter' && event.currentTarget.blur()}
              value={form.lastName}
            />
          </div>

          <ReadonlyRow label={translate('settings.profile.items.email')} value={user?.email} />

          <ReadonlyRow label={translate('settings.profile.items.authMethod')} value={authMethods} />
        </div>

        {canEditAdmin && (
          <label className={'flex w-fit cursor-pointer items-center gap-2 pt-1 text-sm text-foreground'}>
            <input
              checked={!!user?.isAdmin}
              className={'h-4 w-4 cursor-pointer accent-primary'}
              onChange={event => persist({ isAdmin: event.target.checked })}
              type={'checkbox'}
            />

            {translate('administration.admin')}
          </label>
        )}

        <div className={'flex items-center justify-end gap-3 border-t border-border pt-4'}>
          {status === 'saving' && (
            <span className={'flex items-center gap-1 text-xs text-muted-foreground'}>
              <Loader2 className={'h-3 w-3 animate-spin'} /> {translate('settings.profile.saving')}
            </span>
          )}

          {status === 'saved' && !isDirty && (
            <span className={'flex items-center gap-1 text-xs text-primary'}>
              <Check className={'h-3 w-3'} /> {translate('settings.profile.saved')}
            </span>
          )}

          <Button disabled={!isDirty || status === 'saving'} onClick={saveAll} type={'button'}>
            {translate('common.actions.save')}
          </Button>
        </div>
      </div>

      {/* Security */}
      <div className={'rounded-xl border border-border bg-card p-5 shadow-sm'}>
        <div className={'flex items-center justify-between gap-4'}>
          <div className={'min-w-0'}>
            <div className={'text-sm font-semibold text-foreground'}>
              {translate('settings.profile.security.title')}
            </div>

            <div className={'mt-1 text-sm text-muted-foreground'}>
              {translate('settings.profile.security.description')}
            </div>
          </div>

          <Button className={'shrink-0'} onClick={() => setPasswordOpen(true)} type={'button'} variant={'outline'}>
            <KeyRound className={'h-4 w-4'} /> {translate('settings.profile.security.reset')}
          </Button>
        </div>
      </div>

      {cropSrc && (
        <AvatarCropDialog
          imageSrc={cropSrc}
          onCancel={() => !savingPhoto && setCropSrc(null)}
          onCropped={onCropped}
          open={!!cropSrc}
          saving={savingPhoto}
        />
      )}

      <PasswordDialog onOpenChange={setPasswordOpen} onSave={onSetPassword} open={passwordOpen} />
    </div>
  );
}
